import React, { useRef, useState } from "react";
import { Upload, Camera, FileText, X, Download } from "lucide-react";
import { Modal } from "./primitives.jsx";
import { uploadFile, useSignedUrl, isImagePath, MAX_BYTES } from "../lib/storage.js";

/** File picker that uploads immediately and hands back the storage path. */
export function UploadBtn({ label, accept, folder, onUploaded, small, camera, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after a failure
    if (!file) return;
    setErr("");
    setBusy(true);
    try {
      const path = await uploadFile(file, folder);
      onUploaded({ path, name: file.name });
    } catch (error) {
      setErr(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture={camera ? "environment" : undefined}
        style={{ display: "none" }}
        onChange={pick}
      />
      <button
        type="button"
        className={"btn ghost" + (small ? " sm" : "")}
        disabled={busy || disabled}
        onClick={() => inputRef.current?.click()}
      >
        {camera ? <Camera size={15} /> : <Upload size={15} />} {busy ? "Uploading…" : label}
      </button>
      {err && (
        <div className="errnote" style={{ flexBasis: "100%" }}>
          {err}
        </div>
      )}
    </>
  );
}

/** Thumbnail for an image path; a document icon for anything else. */
export function AttachThumb({ path, size = 52, onClick }) {
  const { url, loading, error } = useSignedUrl(path);

  if (!path) return null;
  if (!isImagePath(path)) {
    return (
      <div
        className="thumb"
        style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", cursor: onClick ? "pointer" : "default" }}
        onClick={onClick}
        title="Document"
      >
        <FileText size={Math.round(size / 2.4)} />
      </div>
    );
  }
  if (loading) return <div className="thumb" style={{ width: size, height: size, background: "#eee" }} />;
  if (error) {
    return (
      <div
        className="thumb"
        style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--red)", fontSize: 10, textAlign: "center" }}
        title={error}
      >
        !
      </div>
    );
  }
  return (
    <img
      className="thumb"
      src={url}
      alt=""
      style={{ width: size, height: size, cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    />
  );
}

/** Full-size viewer. Non-images get a download link rather than a broken frame. */
export function AttachViewer({ path, name, onClose }) {
  const { url, loading, error } = useSignedUrl(path, 600);

  return (
    <Modal title={name || "Attachment"} onClose={onClose} wide>
      {loading && <div className="empty">Loading…</div>}
      {error && <div className="notice red">{error}</div>}
      {url && isImagePath(path) && (
        <div style={{ textAlign: "center" }}>
          <img src={url} alt={name || ""} className="thumb-lg" />
        </div>
      )}
      {url && !isImagePath(path) && (
        <div className="empty">
          <b>{name || "Document"}</b>
          <div>This file type opens outside the app.</div>
          <div style={{ marginTop: 14 }}>
            <a className="btn primary" href={url} target="_blank" rel="noreferrer">
              <Download size={15} /> Open
            </a>
          </div>
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--muted)", textAlign: "center" }}>
        This link is temporary and expires in a few minutes — the file itself is private.
      </div>
    </Modal>
  );
}

/** Upload / preview / remove for a single optional file on a record. */
export function AttachField({ path, name, folder, accept, label, camera, onChange, onView }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      {path && <AttachThumb path={path} onClick={() => onView?.({ path, name })} />}
      {path && name && <span style={{ fontSize: 13 }}>{name}</span>}
      <UploadBtn
        small
        camera={camera}
        label={path ? "Replace" : label}
        accept={accept}
        folder={folder}
        onUploaded={({ path: p, name: n }) => onChange({ path: p, name: n })}
      />
      {path && (
        <button type="button" className="btn ghost sm" onClick={() => onChange({ path: null, name: null })}>
          <X size={14} /> Remove
        </button>
      )}
    </div>
  );
}

export { MAX_BYTES };
